import { RTC_PROTOCOL_V1 } from '../protocol/constants.js';
import { RtcError, silentLogger, } from '../protocol/types.js';
import { errorFromResponse, networkError } from './httpErrors.js';
const trimTrailingSlash = (url) => url.replace(/\/+$/, '');
const isLivekitTransport = (value) => !!value &&
    typeof value === 'object' &&
    value.type === RTC_PROTOCOL_V1.transportType &&
    typeof value.livekit_service_url === 'string' &&
    /^(wss|https):\/\//.test(value.livekit_service_url);
const firstLivekitTransport = (values) => Array.isArray(values) ? values.find(isLivekitTransport) : undefined;
const BROWSER_BLOCKED_TURN_PORT = /:53(\?|$)/;
const toIceServers = (value) => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const { uris, username, password } = value;
    if (!Array.isArray(uris) || typeof username !== 'string' || typeof password !== 'string') {
        return undefined;
    }
    const urls = uris.filter((uri) => typeof uri === 'string' && !BROWSER_BLOCKED_TURN_PORT.test(uri));
    return urls.length > 0 ? [{ urls, username, credential: password }] : undefined;
};
export class TokenClient {
    constructor(options) {
        this.options = options;
        this.baseUrl = trimTrailingSlash(options.homeserverUrl);
        this.logger = options.logger ?? silentLogger;
    }
    async discoverTransport() {
        if (this.discovered) {
            return this.discovered;
        }
        const transport = (await this.fromTransportsEndpoint()) ??
            (await this.fromWellKnown()) ??
            this.fromFallback();
        if (!transport) {
            throw new RtcError('unavailable', 'No LiveKit transport is configured for this homeserver');
        }
        this.discovered = { type: 'livekit', livekit_service_url: transport.livekit_service_url };
        return this.discovered;
    }
    async getToken(request) {
        const body = JSON.stringify({
            url: request.url,
            room_id: request.roomId,
            slot_id: request.slotId,
            member_id: request.memberId,
            ...(request.serverName ? { server_name: request.serverName } : {}),
        });
        const response = await this.authorizedRequest(`${this.baseUrl}${RTC_PROTOCOL_V1.tokenPath}`, 'POST', body, 'get_token');
        if (!response.ok) {
            throw await errorFromResponse(response, 'get_token');
        }
        const parsed = (await response.json());
        if (typeof parsed.jwt !== 'string' || parsed.jwt.length === 0) {
            throw new RtcError('unavailable', 'get_token returned no jwt');
        }
        return { jwt: parsed.jwt, url: typeof parsed.url === 'string' && parsed.url.length > 0 ? parsed.url : request.url };
    }
    async turnServers() {
        try {
            const response = await this.authorizedRequest(`${this.baseUrl}${RTC_PROTOCOL_V1.turnServerPath}`, 'GET', undefined, 'voip/turnServer');
            if (!response.ok) {
                this.logger.warn('rtc.turn.unavailable', { status: response.status });
                return undefined;
            }
            const servers = toIceServers(await response.json());
            if (!servers) {
                this.logger.warn('rtc.turn.unavailable', { status: response.status, reason: 'malformed' });
            }
            return servers;
        }
        catch (error) {
            this.logger.warn('rtc.turn.unavailable', { error: error instanceof RtcError ? error.code : String(error) });
            return undefined;
        }
    }
    async fromTransportsEndpoint() {
        try {
            const response = await this.authorizedRequest(`${this.baseUrl}${RTC_PROTOCOL_V1.transportsPath}`, 'GET', undefined, 'rtc/transports');
            if (!response.ok) {
                this.logger.warn('rtc.transports.unavailable', { status: response.status });
                return undefined;
            }
            const parsed = (await response.json());
            return firstLivekitTransport(parsed.rtc_transports);
        }
        catch (error) {
            if (error instanceof RtcError && error.code === 'unauthorized') {
                throw error;
            }
            this.logger.warn('rtc.transports.failed', { error: String(error) });
            return undefined;
        }
    }
    async fromWellKnown() {
        try {
            const response = await this.options.fetch(`${this.baseUrl}${RTC_PROTOCOL_V1.wellKnownPath}`, { method: 'GET' });
            if (!response.ok) {
                return undefined;
            }
            const parsed = (await response.json());
            return firstLivekitTransport(parsed[RTC_PROTOCOL_V1.wellKnownFociKey]);
        }
        catch (error) {
            this.logger.warn('rtc.wellknown.failed', { error: String(error) });
            return undefined;
        }
    }
    fromFallback() {
        const url = this.options.fallbackSfuUrl;
        return url ? { type: 'livekit', livekit_service_url: url } : undefined;
    }
    async authorizedRequest(url, method, body, context) {
        const send = async () => {
            const token = await this.options.accessTokens.current();
            try {
                return await this.options.fetch(url, {
                    method,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        ...(body ? { 'Content-Type': 'application/json' } : {}),
                    },
                    body,
                });
            }
            catch (error) {
                throw networkError(context, error);
            }
        };
        const first = await send();
        if (first.status !== 401) {
            return first;
        }
        await this.options.accessTokens.onUnauthorized();
        const second = await send();
        if (second.status === 401) {
            throw await errorFromResponse(second, context);
        }
        return second;
    }
}
